import React from 'react';
import { CloudinaryComponentProps, Transformation } from 'cloudinary-react';
import { gqlMediaStorageStatus } from '@minecraft.graphql-types';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import { getEnvironment } from '@minecraft.environment';
import { StyledCloudinaryVideo, StyledVideoThumbnailContainer, StyledPlayVideoIcon } from '../styles';
import { cloudinaryEnvironment, sizeType, VideoAudioThumbnail } from '../common';

const environment = getEnvironment();
const cloudinaryDomain = environment.CLD_DOMAIN;

type Props = Omit<CloudinaryComponentProps, 'transformation'> & {
  className?: string;
  height: number | string;
  isMediaInColdStorage: boolean;
  mediaStorageStatus: gqlMediaStorageStatus;
  type: sizeType;
  width: number | string;
  fileKey: string;
  hidePlayIcon?: boolean;
  transformation?: {
    xAxis: number;
    yAxis: number;
    width: number;
    height: number;
    rotate: number;
    startOffset: string;
  };
  thumbnail?: VideoAudioThumbnail;
  'data-qa-id'?: string;
  'data-testid'?: string;
};

const CloudinaryVideoThumbnail = ({
  className,
  height,
  isMediaInColdStorage,
  mediaStorageStatus,
  type,
  width,
  hidePlayIcon,
  fileKey,
  thumbnail,
  poster,
  'data-qa-id': qaId,
  'data-testid': testId,
  transformation,
  ...rest
}: Props): JSX.Element => {
  let thumbnailFilekey = thumbnail?.fileKey ?? fileKey ?? '';
  let posterData = poster;

  // It's the workaround for migrated video, uploaded videos works as usual
  if (thumbnailFilekey.endsWith('m4v')) {
    thumbnailFilekey = thumbnailFilekey.replace('.m4v', '');
    posterData =
      typeof poster === 'object'
        ? {
            ...poster,
            format: 'm4v',
          }
        : {
            format: 'm4v',
          };
  }

  const cloudName = isMediaInColdStorage
    ? cloudinaryEnvironment().coldStorageCloudName
    : cloudinaryEnvironment().hotStorageCloudName;
  const posterForVideo = isMediaInColdStorage ? poster : posterData;

  return (
    <StyledVideoThumbnailContainer className={className} height={height} width={width} data-testid={testId}>
      {isMediaInColdStorage && !posterData ? (
        // display stored in hot storage thumbnail
        <CloudinaryPhoto
          fileKey={thumbnailFilekey}
          mediaStorageStatus={mediaStorageStatus}
          customSize={[Number(width), Number(height)]}
          background="black"
          showLandscape
          data-qa-id={qaId}
          transformation={transformation}
          {...rest}
        />
      ) : (
        // create JPG thumbnail from video file
        <StyledCloudinaryVideo
          cloudName={cloudName}
          publicId={thumbnailFilekey.replace('.m4v', '')}
          resourceType="video"
          preload="none"
          fetchFormat="jpg"
          $wrapperHeight={height}
          $wrapperWidth={width}
          $isVideo
          poster={posterForVideo}
          {...rest}
          // DO NOT TOUCH, SHOULD NOT BE CHANGED
          secure
          secureDistribution={cloudinaryDomain}
        >
          <Transformation fetchFormat="mp4" videoCodec="h264" quality="auto" />
        </StyledCloudinaryVideo>
      )}
      {!hidePlayIcon && <StyledPlayVideoIcon name="play-circle-filled" />}
    </StyledVideoThumbnailContainer>
  );
};

export default CloudinaryVideoThumbnail;
